<template>
  <RevCard class="p-24">
    <h2 class="heading-2 mb-8">
      {{ homeTranslation.title }}
    </h2>
    <div class="body-1">
      {{ homeTranslation.description }}
    </div>
    <div class="grid-standard mt-32 gap-24">
      <template v-for="profileSection in profileSections">
        <div
          v-if="profileSection.shouldBeDisplayed"
          :key="profileSection.name"
          class="border-static-default-mid rounded-md flex flex-col items-start justify-between border p-24 md:col-span-2 lg:col-span-4"
        >
          <div class="flex w-full items-center justify-between">
            <component :is="getIcon(profileSection.name)" />
            <RevTag
              v-if="profileSection.shouldDisplayWarningTag"
              :label="i18n(translations.toDoLabel)"
              variant="warning"
            />
          </div>
          <h3 class="heading-3 mt-8">
            {{ profileSection.label }}
          </h3>
          <div class="body-1 mt-8">
            {{ profileSection.description }}
          </div>
          <RevButton
            v-if="profileSection.to"
            class="mt-16"
            size="small"
            :to="{ name: profileSection.to }"
            variant="secondary"
          >
            {{ profileSection.buttonLabel }}
          </RevButton>
        </div>
      </template>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevTag } from '@ds/components/Tag'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { PROFILES, PROFILE_ICONS } from '../../Profile.constants'
import {
  getCompanyInfoLink,
  getContactDetailsLink,
  getLegalDetailsLink,
  getUserPermissionsLink,
} from '../../Profile.utils'

import translations from './Home.translations'

const i18n = useI18n()
const { openErrorToast } = useTheToast()
const {
  companyName: sellerName,
  isAdmin,
  onboardingCompleted,
} = useSellerInfoStore()

const { data, error } = await useHttpFetch(sellerProfileAPI.getProfileMetadata)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const homeTranslation = computed(() => {
  if (onboardingCompleted) {
    return {
      title: i18n(translations.establishedSellerTitle, { sellerName }),
      description: i18n(translations.establishedSellerDescription),
      businessDetailsSectionDescription: i18n(
        translations.businessDetailsSectionEstablishedSellerDescription,
      ),
      legalDetailsSectionDescription: i18n(
        translations.legalDetailsSectionEstablishedSellerDescription,
      ),
      contactDetailsSectionDescription: i18n(
        translations.contactDetailsSectionEstablishedSellerDescription,
      ),
      userPermissionsSectionDescription: i18n(
        translations.userPermissionsDetails,
      ),
      businessDetailsSectionLinkLabel: i18n(translations.editLinkLabel),
      legalDetailsSectionLinkLabel: i18n(translations.editLinkLabel),
      contactDetailsSectionLinkLabel: i18n(translations.editLinkLabel),
      userPermissionsDetailsSectionLinkLabel: i18n(
        translations.userPermissionsLinkLabel,
      ),
    }
  }

  return {
    title: i18n(translations.newSellerTitle, { sellerName }),
    description: i18n(translations.newSellerDescription),
    businessDetailsSectionDescription: i18n(
      translations.businessDetailsSectionNewSellerDescription,
    ),
    legalDetailsSectionDescription: i18n(
      translations.legalDetailsSectionNewSellerDescription,
    ),
    contactDetailsSectionDescription: i18n(
      translations.contactDetailsSectionNewSellerDescription,
    ),
    userPermissionsSectionDescription: i18n(
      translations.userPermissionsDetails,
    ),
    businessDetailsSectionLinkLabel: i18n(translations.confirmLinkLabel),
    legalDetailsSectionLinkLabel: i18n(translations.addLinkLabel),
    contactDetailsSectionLinkLabel: i18n(translations.addLinkLabel),
    userPermissionsDetailsSectionLinkLabel: i18n(
      translations.userPermissionsLinkLabel,
    ),
  }
})

const profileSections = computed(() => [
  {
    name: PROFILES.BUSINESS_DETAILS,
    label: i18n(translations.businessDetailsSectionTitle),
    description: homeTranslation.value.businessDetailsSectionDescription,
    to: getCompanyInfoLink(onboardingCompleted),
    buttonLabel: homeTranslation.value.businessDetailsSectionLinkLabel,
    shouldBeDisplayed: true,
    shouldDisplayWarningTag: false,
  },
  {
    name: PROFILES.LEGAL_DETAILS,
    label: i18n(translations.legalDetailsSectionTitle),
    description: homeTranslation.value.legalDetailsSectionDescription,
    to: getLegalDetailsLink(onboardingCompleted),
    buttonLabel: homeTranslation.value.legalDetailsSectionLinkLabel,
    shouldBeDisplayed: true,
    shouldDisplayWarningTag:
      data.value?.isMissingLegalDetails || data.value?.isMissingLegalDocuments,
  },
  {
    name: PROFILES.CONTACT_DETAILS,
    label: i18n(translations.contactDetailsSectionTitle),
    description: homeTranslation.value.contactDetailsSectionDescription,
    to: getContactDetailsLink(onboardingCompleted),
    buttonLabel: homeTranslation.value.contactDetailsSectionLinkLabel,
    shouldBeDisplayed: true,
    shouldDisplayWarningTag: false,
  },
  {
    name: PROFILES.USER_PERMISSIONS,
    label: i18n(translations.userPermissionsSectionTitle),
    description: homeTranslation.value.userPermissionsSectionDescription,
    to: getUserPermissionsLink(onboardingCompleted),
    buttonLabel: homeTranslation.value.userPermissionsDetailsSectionLinkLabel,
    shouldBeDisplayed: isAdmin,
    shouldDisplayWarningTag: false,
  },
])

function getIcon(sectionName: string) {
  return PROFILE_ICONS[sectionName]
}
</script>
